import { useEffect, useMemo, useState } from "react";
import { CreateAdminAccountRequest } from "../../../models/RequestModels/CreateAdminAccountRequest";
import { CreateAdminCleanerRequest } from "../../../models/RequestModels/CreateAdminCleanerRequest";
import { UpdateAdminSubscriptionRequest } from "../../../models/RequestModels/UpdateAdminSubscriptionRequest";
import { AdminUserResponse } from "../../../models/ResponseModels/AdminUserResponse";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { showMessage } from "../../../store/slices/appMessageSlice";
import {
  clearAdminAccountsError,
  clearSelectedAccount,
  createAdminAccount,
  createAdminCleaner,
  deleteAdminAccount,
  deleteAdminUser,
  fetchAdminAccount,
  fetchAdminAccounts,
  fetchAdminSubscriptions,
  setAdminAccountFilters,
  updateAdminSubscription,
  updateAdminUserStatus,
  saveAdminProperty,
  saveAdminUnit,
} from "../../../store/slices/adminAccountsSlice";
import { UpsertAdminPropertyRequest } from "../../../models/RequestModels/UpsertAdminPropertyRequest";
import { UpsertAdminUnitRequest } from "../../../models/RequestModels/UpsertAdminUnitRequest";
import { AdminPropertyResponse } from "../../../models/ResponseModels/AdminPropertyResponse";
import { AdminUnitResponse } from "../../../models/ResponseModels/AdminUnitResponse";

export const useAccountsScreen = () => {
  const dispatch = useAppDispatch();

  const {
    accounts,
    selectedAccount,
    subscriptions,
    pagination,
    filters,
    loading,
    subscriptionsLoading,
    detailsLoading,
    saving,
    error,
  } = useAppSelector((state) => state.adminAccounts);

  const [createAccountOpen, setCreateAccountOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [subscriptionOpen, setSubscriptionOpen] = useState(false);
  const [cleanerOpen, setCleanerOpen] = useState(false);
  const [propertyOpen, setPropertyOpen] = useState(false);

  const [selectedProperty, setSelectedProperty] =
    useState<AdminPropertyResponse | null>(null);

  const [unitOpen, setUnitOpen] = useState(false);

  const [selectedUnit, setSelectedUnit] = useState<AdminUnitResponse | null>(
    null,
  );

  const [initialUnitPropertyId, setInitialUnitPropertyId] = useState<
    number | null
  >(null);

  useEffect(() => {
    dispatch(fetchAdminSubscriptions());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchAdminAccounts(filters));
  }, [dispatch, filters]);

  useEffect(() => {
    return () => {
      dispatch(clearAdminAccountsError());
      dispatch(clearSelectedAccount());
    };
  }, [dispatch]);

  const stats = useMemo(() => {
    const total = pagination?.total ?? accounts.length;
    const active = accounts.filter(
      (account) => account.subscription_status === "active",
    ).length;
    const trial = accounts.filter(
      (account) => account.subscription_status === "trial",
    ).length;
    const expired = accounts.filter(
      (account) =>
        account.subscription_status === "expired" ||
        account.subscription_status === "cancelled",
    ).length;

    return {
      total,
      active,
      trial,
      expired,
    };
  }, [accounts, pagination]);

  const subscriptionFilterOptions = useMemo(
    () => [
      { label: "All plans", value: "" },
      ...subscriptions.map((subscription) => ({
        label: subscription.name,
        value: subscription.code,
      })),
    ],
    [subscriptions],
  );

  const updateFilter = (key: string, value: string | number) => {
    dispatch(
      setAdminAccountFilters({
        [key]: value,
        page: key === "page" ? value : 1,
      }),
    );
  };

  const refreshAccounts = () => {
    dispatch(fetchAdminSubscriptions());
    dispatch(fetchAdminAccounts(filters));
  };

  const openAccountDetails = async (accountId: number) => {
    const result = await dispatch(fetchAdminAccount(accountId));

    if (fetchAdminAccount.fulfilled.match(result)) {
      setDetailsOpen(true);
    }
  };

  const handleCreateAccount = async (body: CreateAdminAccountRequest) => {
    const result = await dispatch(createAdminAccount(body));

    if (createAdminAccount.fulfilled.match(result)) {
      setCreateAccountOpen(false);
      dispatch(
        showMessage({
          title: "Account created",
          message: "Owner account was created successfully.",
          type: "success",
        }),
      );
      dispatch(fetchAdminAccounts(filters));
    }
  };

  const handleUpdateSubscription = async (
    body: UpdateAdminSubscriptionRequest,
  ) => {
    if (!selectedAccount) {
      return;
    }

    const result = await dispatch(
      updateAdminSubscription({
        accountId: selectedAccount.id,
        body,
      }),
    );

    if (updateAdminSubscription.fulfilled.match(result)) {
      setSubscriptionOpen(false);
      dispatch(
        showMessage({
          title: "Subscription updated",
          message: "Account subscription was updated successfully.",
          type: "success",
        }),
      );
      dispatch(fetchAdminAccounts(filters));
    }
  };

  const handleCreateCleaner = async (body: CreateAdminCleanerRequest) => {
    if (!selectedAccount) {
      return;
    }

    const result = await dispatch(
      createAdminCleaner({
        accountId: selectedAccount.id,
        body,
      }),
    );

    if (createAdminCleaner.fulfilled.match(result)) {
      setCleanerOpen(false);
      dispatch(
        showMessage({
          title: "Team member added",
          message: "Team member account was created successfully.",
          type: "success",
        }),
      );
      dispatch(fetchAdminAccounts(filters));
    }
  };

  const openCreateProperty = () => {
    setSelectedProperty(null);
    setPropertyOpen(true);
  };

  const openEditProperty = (property: AdminPropertyResponse) => {
    setSelectedProperty(property);
    setPropertyOpen(true);
  };

  const handleSaveProperty = async (body: UpsertAdminPropertyRequest) => {
    if (!selectedAccount) {
      return;
    }

    const result = await dispatch(
      saveAdminProperty({
        accountId: selectedAccount.id,
        propertyId: selectedProperty?.id,
        body,
      }),
    );

    if (saveAdminProperty.fulfilled.match(result)) {
      setPropertyOpen(false);
      setSelectedProperty(null);

      dispatch(
        showMessage({
          title: selectedProperty ? "Property updated" : "Property added",

          message: selectedProperty
            ? "Property was updated successfully."
            : "Property was added successfully.",

          type: "success",
        }),
      );

      dispatch(fetchAdminAccounts(filters));
    }
  };

  const openCreateUnit = (propertyId?: number) => {
    setSelectedUnit(null);

    setInitialUnitPropertyId(propertyId || null);

    setUnitOpen(true);
  };

  const openEditUnit = (unit: AdminUnitResponse) => {
    setSelectedUnit(unit);

    setInitialUnitPropertyId(unit.property_id);

    setUnitOpen(true);
  };

  const handleSaveUnit = async (body: UpsertAdminUnitRequest) => {
    if (!selectedAccount) {
      return;
    }

    const result = await dispatch(
      saveAdminUnit({
        accountId: selectedAccount.id,
        unitId: selectedUnit?.id,
        body,
      }),
    );

    if (saveAdminUnit.fulfilled.match(result)) {
      setUnitOpen(false);
      setSelectedUnit(null);
      setInitialUnitPropertyId(null);

      dispatch(
        showMessage({
          title: selectedUnit ? "Unit updated" : "Unit added",

          message: selectedUnit
            ? "Unit and calendar links were updated successfully."
            : "Unit and calendar links were added successfully.",

          type: "success",
        }),
      );

      dispatch(fetchAdminAccounts(filters));
    }
  };

  const handleToggleUserStatus = async (user: AdminUserResponse) => {
    if (!selectedAccount) {
      return;
    }

    const result = await dispatch(
      updateAdminUserStatus({
        userId: user.id,
        isActive: user.is_active === false,
        accountId: selectedAccount.id,
      }),
    );

    if (updateAdminUserStatus.fulfilled.match(result)) {
      dispatch(
        showMessage({
          title: "User updated",
          message: `User was ${
            user.is_active === false ? "activated" : "deactivated"
          } successfully.`,
          type: "success",
        }),
      );
      dispatch(fetchAdminAccounts(filters));
    }
  };

  const handleDeleteUser = async (user: AdminUserResponse) => {
    if (!selectedAccount) {
      return;
    }

    const confirmed = window.confirm(
      `Permanently delete ${user.email}? This cannot be undone.`,
    );

    if (!confirmed) {
      return;
    }

    const result = await dispatch(
      deleteAdminUser({
        userId: user.id,
        accountId: selectedAccount.id,
      }),
    );

    if (deleteAdminUser.fulfilled.match(result)) {
      dispatch(
        showMessage({
          title: "User deleted",
          message: "User was permanently deleted.",
          type: "success",
        }),
      );

      dispatch(fetchAdminAccounts(filters));
    }
  };

  const handleDeleteAccount = async () => {
    if (!selectedAccount) {
      return;
    }

    const confirmed = window.confirm(
      `Permanently delete account "${selectedAccount.name}" and all related data? This cannot be undone.`,
    );

    if (!confirmed) {
      return;
    }

    const result = await dispatch(deleteAdminAccount(selectedAccount.id));

    if (deleteAdminAccount.fulfilled.match(result)) {
      setDetailsOpen(false);

      dispatch(
        showMessage({
          title: "Account deleted",
          message: "Account and all related data were permanently deleted.",
          type: "success",
        }),
      );

      dispatch(fetchAdminAccounts(filters));
    }
  };

  const closeDetails = () => {
    setDetailsOpen(false);
    setPropertyOpen(false);
    setUnitOpen(false);
    setSelectedProperty(null);
    setSelectedUnit(null);
    setInitialUnitPropertyId(null);

    dispatch(clearSelectedAccount());
  };

  return {
    accounts,
    selectedAccount,
    subscriptions,
    subscriptionFilterOptions,
    pagination,
    filters,
    loading,
    subscriptionsLoading,
    detailsLoading,
    saving,
    error,
    stats,
    createAccountOpen,
    setCreateAccountOpen,
    detailsOpen,
    closeDetails,
    subscriptionOpen,
    setSubscriptionOpen,
    cleanerOpen,
    setCleanerOpen,
    updateFilter,
    refreshAccounts,
    openAccountDetails,
    handleCreateAccount,
    handleUpdateSubscription,
    handleCreateCleaner,
    handleToggleUserStatus,
    handleDeleteUser,
    handleDeleteAccount,
    propertyOpen,
    setPropertyOpen,
    selectedProperty,

    unitOpen,
    setUnitOpen,
    selectedUnit,
    initialUnitPropertyId,

    openCreateProperty,
    openEditProperty,
    handleSaveProperty,

    openCreateUnit,
    openEditUnit,
    handleSaveUnit,
  };
};
